import styled, { keyframes } from 'styled-components';

import {
    designColor,
    floorGameDuration as duration,
    floorGameEasing as easing,
    styledInteraction,
    styledMedia,
    styledReset,
} from '@pkg/primitives';

const tokens = {
    duration: {
        bounce: 240,
        fillHeight: 280,
    },
    delay: {
        bounce: 200,
    },
};

const bounce = keyframes`
  0%,
  100% {
    scale: 1;
  }

  40% {
    scale: 1.2;
  }

  80% {
    scale: 0.8;
  }
`;

// All size dimensions are relative to the parent's `font-size`.
// This component expects the consumer to specify `font-size`.
export const StyledStarAction = styled.button.attrs({ type: 'button' })`
    ${styledReset.borderBox};
    ${styledReset.button};

    --star-action-fill-height: 100%;
    --star-action-fill-opacity: 1;
    --star-action-outline-color: rgb(255, 255, 255, 0.5);

    --star-action-bg-color: rgb(255, 255, 255, 0.06);
    --star-action-bg-color-active: rgb(255, 255, 255, 0.1);

    display: grid;
    align-items: center;
    justify-items: center;
    padding: 0.2em;
    font-size: inherit;
    border-radius: clamp(2px, 0.2em, 40px);
    background-color: var(--star-action-bg-color);
    outline: 2px solid transparent !important;
    outline-offset: 2px;
    transition-property: background-color, outline-color;
    transition-duration: ${duration.normal}ms;
    transition-timing-function: ${easing.cubic};

    &[aria-pressed] {
        --star-action-fill-height: 0%;
        --star-action-outline-color: rgb(255, 255, 255, 1);

        --star-action-bg-color: ${designColor.brandPurple};
        --star-action-bg-color-active: ${designColor.brandPurpleDarker};

        &:hover {
            --star-action-fill-opacity: 0.4;
        }

        &:active {
            --star-action-fill-opacity: 0.6;
        }
    }

    &:disabled {
        ${styledInteraction.interactionDisable};
        --star-action-fill-opacity: 0.2;
        --star-action-outline-color: rgb(255, 255, 255, 0.2);
        --star-action-bg-color: rgb(255, 255, 255, 0.04);
    }

    &:focus-visible {
        outline-color: ${designColor.brandPurple} !important;
    }

    &:hover {
        --star-action-outline-color: rgb(255, 255, 255, 1);
        background-color: var(--star-action-bg-color-active);
    }

    &:active {
        --star-action-outline-color: rgb(255, 255, 255, 0.8);
        background-color: var(--star-action-bg-color);
    }
`;

export const Interior = styled.div`
    ${styledMedia.iconWrapper};
    width: 0.6em;
    height: 0.6em;
    animation-delay: ${tokens.delay.bounce}ms;
    animation-duration: ${tokens.duration.bounce}ms;
    animation-timing-function: ease-out;

    ${StyledStarAction}[aria-pressed] & {
        // TODO: We do not want this to animate on "first render",
        // so we might need to move this animation to 'framer-motion'.
        animation-name: ${bounce};
    }

    svg {
        grid-area: 1 / 1;
    }

    // Star fill
    svg:first-child {
        z-index: 1;
        position: relative;
        color: rgb(255, 255, 255, 1);
        opacity: var(--star-action-fill-opacity);
        clip-path: inset(var(--star-action-fill-height) 0 0 0);
        transition-property: opacity, clip-path;
        transition-duration: ${tokens.duration.fillHeight}ms;
        transition-timing-function: ${easing.cubic};
    }

    // Star outline
    svg:last-child {
        z-index: 2;
        position: relative;
        fill: var(--star-action-outline-color);
        transition: fill ${duration.normal}ms ${easing.cubic};
    }
`;
